import React, { KeyboardEvent, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { MenuItem, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StyleSettings } from "../../styles/StyleSettings";

dayjs.extend(isoWeek);

/**
 * 기준주차(연도 + 주차) 선택 컨트롤.
 *
 * - cd_param 은 "yy,no_week" 처럼 두 개를 콤마로 적는다(SnComboTextBox 와 같은 2값 컨트롤).
 * - ISO 8601 기준이라 한 해의 주차 수가 52 또는 53으로 해마다 다르다.
 *   선택한 연도의 실제 주차 수까지만 목록에 나오고, 연도를 바꿔 주차가 넘치면 마지막 주차로 맞춘다.
 */
interface SnYearWeekPickerProps {
  /** "2026,33" 형태. 비어 있으면 오늘이 속한 ISO 연/주차로 시작한다. */
  defaultValue?: string;
  onValueChanged?: (yy: string, no_week: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
}

/** 연도칸 폭(px) — "YYYY" 네 글자 + 달력 버튼. */
const YEAR_WIDTH = 100;
/** 주차칸 폭(px) — "53주차" + 펼치기 화살표. 목록은 MenuProps.minWidth 로 따로 넓다. */
const WEEK_WIDTH = 95;
/** 12월 28일은 ISO 규칙상 항상 그 해 마지막 주차에 속한다. */
const lastWeekOfYear = (year: number) =>
  dayjs(`${year}-12-28`).isoWeek();

const SnYearWeekPicker = React.memo(
  ({
    defaultValue,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
  }: SnYearWeekPickerProps) => {
    const today = dayjs();
    const [yy, week] = (defaultValue ?? "").split(",").map((v) => v.trim());

    // 연도는 조회 SQL이 앞 4자리만 쓰므로("left(:yy,4)") 날짜가 들어와도 연도만 취한다.
    const [year, setYear] = useState<number>(
      Number(yy?.substring(0, 4)) || today.isoWeekYear()
    );
    const [noWeek, setNoWeek] = useState<number>(
      Number(week) || today.isoWeek()
    );
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");

    useEffect(() => {
      setBorderWidth(isRequired ? "2px" : "1px");
    }, [isRequired]);

    useEffect(() => {
      setBackgroundColor(isEditable ? "white" : "#F1F1F1");
    }, [isEditable]);

    // 화면이 처음 뜰 때도 조회조건에 기본 연/주차가 들어가 있어야 한다.
    useEffect(() => {
      notify(year, noWeek);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 화면 쪽에서 조회조건을 바꿔 넣는 경우(신규 초기화 등)를 따라간다. 빈 값이면 현재 선택을 유지한다.
    useEffect(() => {
      const [nextYy, nextWeek] = (defaultValue ?? "")
        .split(",")
        .map((v) => v.trim());
      const parsedYear = Number(nextYy?.substring(0, 4));
      const parsedWeek = Number(nextWeek);
      if (parsedYear) setYear(parsedYear);
      if (parsedWeek) setNoWeek(parsedWeek);
    }, [defaultValue]);

    const notify = (nextYear: number, nextWeek: number) => {
      onValueChanged?.(String(nextYear), String(nextWeek).padStart(2, "0"));
    };

    const handleYearChange = (value: Dayjs | null) => {
      if (!value || !value.isValid()) return;

      const nextYear = value.year();
      // 53주차를 고른 뒤 52주뿐인 연도로 바꾸면 없는 주차가 되므로 마지막 주차로 맞춘다.
      const nextWeek = Math.min(noWeek, lastWeekOfYear(nextYear));

      setYear(nextYear);
      setNoWeek(nextWeek);
      notify(nextYear, nextWeek);
    };

    const handleWeekChange = (value: string) => {
      const nextWeek = Number(value);
      setNoWeek(nextWeek);
      notify(year, nextWeek);
    };

    const handleKeyDown = async (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === "Enter") {
        await (e.target as HTMLElement).blur();
        e.preventDefault();
        onFocus?.(isSearchPanel ? "current" : "next");
      } else if (e.key === "Tab") {
        await (e.target as HTMLElement).blur();
        e.preventDefault();
        onFocus?.("next");
      }
    };

    const controlSx = {
      "& .MuiInputBase-root": {
        height: StyleSettings.control_height,
        fontSize: StyleSettings.font,
      },
      "& .MuiSvgIcon-root": {
        fontSize: StyleSettings.font,
      },
      backgroundColor: backgroundColor,
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderWidth: borderWidth, // 테두리 두께 설정
        },
        "&:hover fieldset": {
          borderWidth: borderWidth, // 호버 시 두께 유지
        },
        "&.Mui-focused fieldset": {
          borderWidth: borderWidth, // 포커스 시 두께 유지
        },
      },
    };

    const weeks = Array.from({ length: lastWeekOfYear(year) }, (_, i) => i + 1);
    // 각 주차가 실제 어떤 날짜(월~일)인지. 1월 4일은 항상 그 해 1주차라 기준으로 삼는다.
    const weekDateRange = (w: number) => {
      const start = dayjs(`${year}-01-04`).isoWeek(w).startOf("isoWeek");
      return `${start.format("MM월 DD일")} ~ ${start
        .endOf("isoWeek")
        .format("MM월 DD일")}`;
    };
    // 바깥쪽 라벨: 선택된 주차의 날짜 범위(간단 표기 MM/DD~MM/DD)
    const selectedStart = dayjs(`${year}-01-04`)
      .isoWeek(noWeek)
      .startOf("isoWeek");
    const selectedRange = `${selectedStart.format("MM/DD")}~${selectedStart
      .endOf("isoWeek")
      .format("MM/DD")}`;

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {/* 연도(YYYY)·주차(??주차)는 내용이 고정 길이라 칸 폭에 맞춰 늘리지 않고 딱 맞게 고정한다. */}
          <div style={{ width: YEAR_WIDTH, flexShrink: 0 }}>
            <DatePicker
              value={dayjs(`${year}-01-04`)}
              views={["year"]}
              format="YYYY"
              onChange={handleYearChange}
              onAccept={() => setTimeout(() => onFocus?.("current"), 10)}
              disabled={isReadOnly}
              sx={controlSx}
              slotProps={{
                textField: {
                  inputRef: inputRef,
                  fullWidth: true,
                  placeholder: "",
                  helperText: "",
                  onKeyDown: handleKeyDown,
                  inputProps: { "aria-label": "연도", readOnly: true },
                },
                openPickerButton: {
                  tabIndex: -1, // Tab 키로 포커스 안 가게 설정
                },
              }}
            />
          </div>
          <div style={{ width: WEEK_WIDTH, flexShrink: 0 }}>
            <TextField
              select
              fullWidth
              value={String(noWeek)}
              onChange={(e) => handleWeekChange(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isReadOnly}
              sx={controlSx}
              slotProps={{
                htmlInput: { "aria-label": "주차" },
                select: {
                  renderValue: (value: any) => `${value}주차`,
                  MenuProps: {
                    slotProps: { paper: { sx: { maxHeight: 250, minWidth: 220 } } },
                  },
                },
              }}
            >
              {weeks.map((w) => (
                <MenuItem
                  key={w}
                  value={String(w)}
                  sx={{ fontSize: StyleSettings.font, whiteSpace: "nowrap" }}
                >
                  {w}주차 ({weekDateRange(w)})
                </MenuItem>
              ))}
            </TextField>
          </div>
          {/* 날짜범위는 셀 안에서 줄어들게(ellipsis) 제약한다. flex 0 1 auto + minWidth 0 이라
              넓으면 전체 표시, 칸이 좁으면 2줄로 넘치거나(행높이 증가) 셀 밖으로 삐지는 대신
              말줄임 처리 → 항상 한 줄 유지, 옆 컨트롤(업무영역 등)·세로 영역에 영향 없음. */}
          <div
            style={{
              flex: "0 1 auto",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: StyleSettings.font,
              color: "#6b7280",
            }}
          >
            {selectedRange}
          </div>
        </div>
      </LocalizationProvider>
    );
  }
);

export default SnYearWeekPicker;
